import React, { useMemo, useState } from "react";
import { Box, Chip, Skeleton, Typography, useTheme } from "@mui/material";
import ButtonComponent from "../../../../Component/Button/Button";
import { useNavigate } from "react-router-dom";
import { NavRoutes } from "../../../../Component/Navbar/NavRoutes";
import { API_endpoints } from "../../../../API/API_Endpoints";
import { useTranslation } from "react-i18next";
import premiumTruckLogo from "../../../../Assets/images/p2.webp";
import RatingStar from "../../../../Component/Rating";

const TruckCard = ({ data, title, openModal, index }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const companyid = localStorage.getItem("Fiesta_compID");
  const isOwner = Number(companyid) === Number(data?.company_id);

  const isFoodTruck = useMemo(
    () =>
      !!data?.truck_name ||
      !!data?.food_truck_image?.length ||
      !!data?.requirement,
    [data]
  );

  const imageUrl = useMemo(() => {
    if (isFoodTruck && data?.food_truck_image?.length)
    {
      return API_endpoints.baseURL + "/" + data.food_truck_image[0].image;
    } else if (data?.image?.length)
    {
      return API_endpoints.baseURL + "/" + data.image[0].image;
    }
    return "";
  }, [data, isFoodTruck]);

  const handleCardClick = () => {
    if (isOwner)
    {
      if (index === 0)
      {
        navigate(NavRoutes.MY_COMPANY);
      } else
      {
        if (isFoodTruck)
        {
          openModal(data);
        } else
        {
          navigate(NavRoutes.MY_COMPANY);
        }
      }
    } else
    {
      if (index === 0)
      {
        navigate(`${NavRoutes.COMPANY_DETAILS}/${data?.company_id}`);
      } else
      {
        if (isFoodTruck)
        {
          navigate(`${NavRoutes.TRUCK_DETAILS}/${data?.id}`, {
            state: { id: data?.id, company_id: data?.company_id },
          });
        } else
        {
          navigate(`${NavRoutes.COMPANY_DETAILS}/${data?.company_id}`);
        }
      }
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 3,
        boxShadow: 3,
        mt: 2,
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.main,
        transition: "transform 0.3s ease, box-shadow 0.3s ease",

        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardImage
        imageUrl={imageUrl}
        isPremium={data?.subscription?.truck_limit !== 1}
        title={data?.title || "Truck Image"}
      />

      <Box sx={{ px: 2, py: 1 }}>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.dark,
            fontWeight: 700,
            fontSize: "18px",
          }}
        >
          {index === 0
            ? data?.company_name
            : isFoodTruck
              ? data?.truck_name
              : data?.company_name}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {/* Chip Text */}
          <Typography
            sx={{
              color: theme.palette.text.darkOrange,
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {isFoodTruck
              ? t("explore.chipFoodTruck")
              : t("explore.chipCaterer")}
          </Typography>

          {/* Rating Block */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 3,
              visibility: data?.avg_rating ? "visible" : "hidden",
            }}
          >
            <RatingStar rating={data?.avg_rating || 0} />
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
                opacity: 0.8,
                ml: 0.5,
              }}
            >
              ({formatRatingCount(data?.total_rating || 0)})
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            // px: 2,
            backgroundColor: theme.palette.background.main,
            fontWeight: 500,
            my: 1,
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.dark,
              fontWeight: 500,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "15px",
              lineHeight: 1.4,
              minHeight: "42px",
            }}
          >
            {isFoodTruck ? data?.description : data?.about}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography
          sx={{
            color: theme.palette.text.blue,
            fontWeight: "600",
            fontSize: "14px",
            mb: 0.5,
          }}
        >
          {t("explore.meal")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {data?.meal
            ?.filter((x) => x?.status === 1)
            ?.slice(0, 2)
            ?.map((x, i) => (
              <Chip
                key={i}
                label={i18n.language === "en-US" ? x?.meal_name : x?.german}
                size="small"
                sx={{
                  backgroundColor: theme.palette.background.label,
                  color: theme.palette.text.label,
                  fontSize: "12px",
                }}
              />
            ))}
          {data?.meal?.filter((x) => x?.status === 1)?.length > 2 && (
            <Chip
              label={`+${data.meal.filter((x) => x.status === 1).length - 2}`}
              size="small"
              sx={{
                backgroundColor: theme.palette.background.secondary,
                color: theme.palette.text.secondary,
                fontSize: "12px",
              }}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <ButtonComponent
          isAnimate
          onClick={handleCardClick}
          label={title}
          sx={{
            width: "100%",
            background: theme.palette.background.btn,
            color: theme.palette.text.defaultLight,
          }}
        />
      </Box>
    </Box>
  );
};

const CardImage = ({ imageUrl, isPremium, title }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: 200, md: 280 },
        minHeight: { xs: 200, sm: 200, md: 280 },
        overflow: "hidden",
        aspectRatio: 1.5,
        borderTopLeftRadius: "10px",
        borderTopRightRadius: "10px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f5f5f5",
      }}
    >
      {isPremium && (
        <img
          src={premiumTruckLogo}
          style={{
            height: "65px",
            width: "65px",
            position: "absolute",
            top: 0,
            right: 0,
            zIndex: 1000,
            transform: "rotate(90deg)",
          }}
          alt="Premium"
        />
      )}

      {!imageLoaded && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{
            borderTopLeftRadius: "10px",
            borderTopRightRadius: "10px",
            backgroundColor: "rgba(0,0,0,0.08)",
            minHeight: "220px",
          }}
        />
      )}

      <img
        src={imageUrl}
        loading="lazy"
        alt={title}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageLoaded(true)}
        style={{
          visibility: imageLoaded ? "visible" : "hidden",
          height: "100%",
          width: "100%",
          objectFit: "cover",
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          position: "relative",
          zIndex: 1,
        }}
      />
    </Box>
  );
};

const formatRatingCount = (count) => {
  if (!count) return 0;
  if (count < 1000) return count;
  if (count < 10000) return (count / 1000).toFixed(1) + "k";
  if (count < 1000000) return Math.floor(count / 1000) + "k";
  return (count / 1000000).toFixed(1) + "M";
};

export default React.memo(TruckCard);
